import { InfoOutlined } from '@mui/icons-material';
import { Box, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';

import { ScoreType } from '@/domain/impact/impact.entity';
import { getScoreLetterFromScore } from '@/domain/impact-calculate/utils/get-score-letter-from-score';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSCard } from '@/ui/components/FSCard';
import { GaugeScore } from '@/ui/components/score-bars/GaugeScore';
import {
  HorizontalScoreBar,
  ScoreBarPosition,
} from '@/ui/components/score-bars/HorizontalScoreBar';
import { language, translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { Fonts } from '@/ui/theme/fonts';
import { getColorForScore } from '@/ui/utils/get-color-for-score';

import {
  ProjectScoresDetailsData,
  ProjectScoresDetailsItemParams,
} from './ProjectScoresDetails.types';

export function ProjectScoresDetailsCard({
  data,
  onClickDetailsItem,
}: {
  data: ProjectScoresDetailsData;
  onClickDetailsItem: (params: ProjectScoresDetailsItemParams) => void;
}) {
  return (
    <FSCard
      sx={{
        backgroundColor: ColorsRaw.anthracite.Anthracite_2,
        borderColor: ColorsRaw.anthracite.Anthracite_2,
        py: 2,
        px: 2,
      }}
    >
      <Grid container alignItems={'center'}>
        <Grid item xs={6}>
          <LeftPart data={data} />
        </Grid>
        <Grid item xs={6}>
          <RightSide data={data} onClickDetailsItem={onClickDetailsItem} />
        </Grid>
      </Grid>
    </FSCard>
  );
}

function LeftPart({ data }: { data: ProjectScoresDetailsData }) {
  if (data.viewMode === 'bar') {
    return <LeftPartBar data={data} />;
  } else {
    return <LeftPartSemiCircle data={data} />;
  }
}

function LeftPartBar({ data }: { data: ProjectScoresDetailsData }) {
  return (
    <Grid container alignItems={'center'} spacing={2} paddingRight={4}>
      <LeftPartBar_Line
        title={data?.labels?.value || ''}
        value={data.value}
        infos={data?.infos?.value || ''}
        color={ColorsRaw.scores.Orange}
        backColor={ColorsRaw.scores.Orange_012}
        position={
          data.value === data.refValue
            ? 'normal'
            : data.value > data.refValue
              ? 'higher'
              : 'lower'
        }
      />
      {!Number.isNaN(data.refValue) && (
        <LeftPartBar_Line
          title={data?.labels?.refValue || ''}
          value={data.refValue}
          infos={data?.infos?.refValue || ''}
          color={ColorsRaw.scores.Anthracite_4}
          backColor={ColorsRaw.scores.Antracite_4012}
          position="normal"
        />
      )}
    </Grid>
  );
}

function LeftPartBar_Line({
  title,
  value,
  infos,
  color,
  backColor,
  position,
}: {
  title: string;
  value: number;
  infos: string;
  color: string;
  backColor: string;
  position: ScoreBarPosition;
}) {
  return (
    <>
      <Grid item xs={5}>
        <Stack direction={'row'} justifyContent={'end'} spacing={1}>
          <Typography sx={{ fontSize: '1rem', fontWeight: '700' }}>
            {title}
          </Typography>
          <Tooltip title={infos}>
            <InfoOutlined />
          </Tooltip>
        </Stack>
      </Grid>
      <Grid item xs={7}>
        <HorizontalScoreBar
          value={value}
          color={color}
          backColor={backColor}
          position={position}
        />
      </Grid>
    </>
  );
}

function LeftPartSemiCircle({ data }: { data: ProjectScoresDetailsData }) {
  return <GaugeScore score={data.value} maxScore={data.maxValue} />;
}

function RightSide({
  data,
  onClickDetailsItem,
}: {
  data: ProjectScoresDetailsData;
  onClickDetailsItem: (params: ProjectScoresDetailsItemParams) => void;
}) {
  const scoreType: ScoreType = data?.detailsValues[0]?.type;
  if (data.detailsValues.length === 0) {
    return <></>;
  }

  const onClickAnswerAll = () => {
    if (scoreType === 'co2') {
      onClickDetailsItem({
        scoreType: scoreType,
        id: '',
        sector: data.sector,
        section: data.detailsValues[0].section,
      });
    }
    if (scoreType === 'ess' || scoreType === 'env') {
      onClickDetailsItem({
        scoreType: scoreType,
        id: data.detailsValues[0].topic,
        sector: undefined,
      });
    }
  };

  const onClickDetails = (
    x: ProjectScoresDetailsData['detailsValues'][number],
  ) => {
    if (scoreType === 'co2') {
      onClickDetailsItem({
        scoreType: scoreType,
        id: x.topic,
        sector: data.sector,
        section: x.section,
      });
    }
    if (scoreType === 'ess' || scoreType === 'env') {
      onClickDetailsItem({
        scoreType: scoreType,
        id: x.topic,
        sector: undefined,
        section: x.section,
      });
    }
  };

  return (
    <Box>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'end'}
      >
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: '900',
            color: ColorsRaw.anthracite.Anthracite_5,
          }}
        >
          {translate('Projects.details')}
        </Typography>
        {!data.answerButtonsAreDisabled && (
          <FSPrimaryButton color="info" onClick={onClickAnswerAll}>
            {translate('Sector.answer-all')}
          </FSPrimaryButton>
        )}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Stack direction={'column'} divider={<Divider />} spacing={2}>
        {data?.detailsValues?.map((x) => (
          <RightSide_Line
            key={x.name}
            name={x.name}
            value={x.value}
            unit={x.unit}
            maxScore={x.maxScore}
            infos={x.infos}
            clickIsDisabled={data.answerButtonsAreDisabled || false}
            onClick={() => onClickDetails(x)}
          />
        ))}
      </Stack>
    </Box>
  );
}

function RightSide_Line({
  name,
  value,
  unit,
  maxScore,
  infos,
  clickIsDisabled,
  onClick,
}: {
  name: string;
  value: number;
  unit: string;
  maxScore?: number;
  infos?: string;
  clickIsDisabled: boolean;
  onClick: () => void;
}) {
  return (
    <Stack
      direction={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}
    >
      <Stack direction={'row'} spacing={1}>
        <Typography sx={{ fontSize: '1rem', fontWeight: '700' }}>
          {name}
        </Typography>
        {infos && (
          <Tooltip title={infos}>
            <InfoOutlined />
          </Tooltip>
        )}
      </Stack>
      <Box
        sx={{
          backgroundColor: maxScore
            ? getColorForScore(getScoreLetterFromScore(value / maxScore))
                .secondary
            : ColorsRaw.anthracite.Anthracite_3,
          '&:hover': {
            filter: 'brightness(1.5)',
          },
          cursor: clickIsDisabled ? null : 'pointer',
          px: 1,
          py: 0.5,
          borderRadius: 2,
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
        }}
        onClick={() => {
          if (clickIsDisabled === false) onClick();
        }}
      >
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: '900',
            fontFamily: Fonts.DarkerGrotesque,
            color: maxScore
              ? getColorForScore(getScoreLetterFromScore(value / maxScore)).main
              : ColorsRaw.basic.White,
          }}
        >
          {value ? value.toLocaleString(language) : '-'} {unit}
        </Typography>
      </Box>
    </Stack>
  );
}
